import type { ReadConfig } from 'api-read-hook';
import type { HydraCollection } from '../../types';

import { useApiRead } from 'api-read-hook';
import { makeQueryString } from '../../util';
import * as MediaUpload from './MediaUpload';

export type V_Detail = {
  '@id': string;
  '@type': 'LibraryContent';
  id: string;
  title: string;
  type: string;
  body: string | null;
  references: string | null;
  question: string | null;
  userAnswer: {
    '@id': string;
    '@type': 'UserSimpleAnswer';
    id: string;
    answer: string;
  } | null;
  thumb: MediaUpload.BaseView | null;
  media: MediaUpload.BaseView | null;
  isSaved: boolean | null;
  isHelpful: boolean | null;
  internalTags: Array<{
    '@id': string;
    '@type': 'Tag';
    userFacingName: string;
  }>;
  userFacingTags: Array<{
    '@id': string;
    '@type': 'Tag';
    userFacingName: string;
  }>;
};

export type V_DetailAsAdmin = {
  '@id': string;
  '@type': 'LibraryContent';
  id: string;
  title: string;
  type: string;
  body: string | null;
  references: string | null;
  question: string | null;
  publishedAt: string | null;
  transcriptEnabled: boolean;
  speakerName: string | null;
  autoTranscript: MediaUpload.BaseView | null;
  autoTranscriptGeneratedAt: string | null;
  manualTranscript: MediaUpload.BaseView | null;
  thumb: MediaUpload.BaseView | null;
  media: MediaUpload.BaseView | null;
  internalTags: Array<{
    '@id': string;
    '@type': 'Tag';
    id: string;
    internalName: string;
  }>;
  userFacingTags: Array<{
    '@id': string;
    '@type': 'Tag';
    id: string;
    internalName: string;
  }>;
};

export type V_ForYou = {
  '@id': string;
  '@type': 'LibraryContent';
  id: string;
  title: string;
  type: string;
  thumb: MediaUpload.BaseView | null;
  isSaved: boolean | null;
  isViewed: boolean | null;
  internalTags: Array<{
    '@id': string;
    '@type': 'Tag';
    userFacingName: string;
  }>;
  userFacingTags: Array<{
    '@id': string;
    '@type': 'Tag';
    userFacingName: string;
  }>;
};

export type V_List = {
  '@id': string;
  '@type': 'LibraryContent';
  id: string;
  title: string;
  type: string;
  thumb: MediaUpload.BaseView | null;
  isSaved: boolean | null;
  isViewed: boolean | null;
  internalTags: Array<{
    '@id': string;
    '@type': 'Tag';
    userFacingName: string;
  }>;
  userFacingTags: Array<{
    '@id': string;
    '@type': 'Tag';
    userFacingName: string;
  }>;
};

export type V_ListAsAdmin = {
  '@id': string;
  '@type': 'LibraryContent';
  id: string;
  title: string;
  type: string;
  isFeatured: boolean;
  publishedAt: string | null;
  updatedAt: string | null;
  internalTags: Array<{
    '@id': string;
    '@type': 'Tag';
    id: string;
    internalName: string;
    category: {
      '@id': string;
      '@type': 'TagCategory';
      name: string;
    };
  }>;
};

export type W = {
  title: string;
  type: string;
  body: string | null;
  references: string | null;
  question: string | null;
  transcriptEnabled: boolean;
  speakerName: string | null;
  manualTranscript: string | null;
  thumb: string | null;
  media: string | null;
  internalTags: Array<string>;
  userFacingTags: Array<string>;
};

export function useReadCollection(
  queryParams?: {
    'exists[publishedAt]'?: boolean;
    internalTags?: string;
    'internalTags[]'?: Array<string>;
    isFeatured?: boolean;
    title?: string;
    type?: string;
    'type[]'?: Array<string>;
  },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_List>>(
    `/library-contents${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadCollectionAdminList(
  queryParams?: {
    'exists[publishedAt]'?: boolean;
    internalTags?: string;
    'internalTags[]'?: Array<string>;
    isFeatured?: boolean;
    title?: string;
    type?: string;
    'type[]'?: Array<string>;
  },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_ListAsAdmin>>(
    `/library-contents/admin-list${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadCollectionForYou(
  queryParams?: {
    'exists[publishedAt]'?: boolean;
    internalTags?: string;
    'internalTags[]'?: Array<string>;
    isFeatured?: boolean;
    title?: string;
    type?: string;
    'type[]'?: Array<string>;
  },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_ForYou>>(
    `/library-contents/for-you${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadItem(id: string, options?: ReadConfig) {
  return useApiRead<V_Detail>(`/library-contents/${id}`, options);
}

export function useReadItemAsAdmin(id: string, options?: ReadConfig) {
  return useApiRead<V_DetailAsAdmin>(
    `/library-contents/${id}/as-admin`,
    options,
  );
}
